import { GroupMarker } from '@/scopes/common/components/Markers/GroupMarker/GroupMarker'
import { Marker as CustomMarker, MarkerProps } from '@/scopes/common/components/Markers/Marker'
import { type Cluster, type Marker, MarkerClusterer, type Renderer } from '@googlemaps/markerclusterer'
import { useMap } from '@vis.gl/react-google-maps'
import React, { useCallback, useEffect, useState } from 'react'
import { renderToStaticMarkup } from 'react-dom/server'

export interface ClusteredTreeMarkersProps {
  markersProps: MarkerProps[]
}

function createClusterContent(count: number) {
  const container = document.createElement('div')
  container.innerHTML = renderToStaticMarkup(
    <GroupMarker count={count} />,
  )
  return container.firstElementChild ?? container
}

function createClusterRenderer(): Renderer {
  return {
    render({ count, position }: Cluster) {
      return new google.maps.marker.AdvancedMarkerElement({
        position,
        content: createClusterContent(count),
        zIndex: 1000 + count,
      })
    },
  }
}

export function MarkerCluster({ markersProps }: ClusteredTreeMarkersProps) {
  const [clusteredMarkers, setClusteredMarkers] = useState<{ [key: string]: Marker }>({})
  const [clusterer, setClusterer] = useState<MarkerClusterer | null>(null)

  const map = useMap()

  useEffect(() => {
    if (!map)
      return

    const newClusterer = new MarkerClusterer({
      map,
      renderer: createClusterRenderer(),
    })

    setClusterer(newClusterer)

    return () => {
      newClusterer.clearMarkers()
      newClusterer.setMap(null)
    }
  }, [map])

  useEffect(() => {
    if (!clusterer)
      return

    clusterer.clearMarkers()
    clusterer.addMarkers(Object.values(clusteredMarkers))
  }, [clusterer, clusteredMarkers])

  // this callback will effectively get passed as ref to the markers to keep
  // tracks of markers currently on the map
  const setMarkerRef = useCallback((marker: Marker | null, key: string) => {
    setClusteredMarkers((markers) => {
      if ((marker && markers[key]) || (!marker && !markers[key]))
        return markers

      if (marker) {
        return { ...markers, [key]: marker }
      }
      else {
        const { [key]: _, ...newMarkers } = markers

        return newMarkers
      }
    })
  }, [])

  return (
    <>
      {markersProps.map(markerProps => (
        <CustomMarker
          key={markerProps.id ?? `${markerProps.lat}_${markerProps.lng}`}
          {...markerProps}
          setMarkerClusterRef={setMarkerRef}
        />
      ))}
    </>
  )
}
